/**
 * 飞机(Telegram) 设置页面 — bot token / username / webhook secret 管理.
 * 无需 SSH 编辑 .env, admin UI 中更新 → DB 保存 → 后端 cache 即时反映.
 */

import { useEffect, useState } from 'react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { PageHeader, SectionCard } from '@/components/admin/page';
import { api } from '../../lib/api';

interface Setting {
  key: string;
  value: string | null;
  source: 'db' | 'env' | 'unset';
  isSecret: boolean;
  hasValue: boolean;
}

const FIELDS: Array<{ key: string; label: string; hint: string; placeholder: string }> = [
  {
    key: 'TELEGRAM_BOT_TOKEN',
    label: 'Bot Token',
    hint: '飞机 @BotFather → /newbot 或 /mybots 中获取的 token (123456:ABC-...)',
    placeholder: '123456:ABC-DEF1234ghIkl...',
  },
  {
    key: 'TELEGRAM_BOT_USERNAME',
    label: 'Bot Username',
    hint: '不含 @ (例: SayKnowWorkAuthBot)',
    placeholder: 'SayKnowWorkAuthBot',
  },
  {
    key: 'TELEGRAM_WEBHOOK_SECRET',
    label: 'Webhook Secret',
    hint: '飞机 → 服务器 webhook 鉴权用随机字符串 (openssl rand -base64 32)',
    placeholder: '随机字符串 (32 字符以上)',
  },
];

export function TelegramSettingsPage() {
  const [settings, setSettings] = useState<Record<string, Setting>>({});
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [message, setMessage] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api<{ settings: Setting[] }>('/v1/admin/platform/settings');
      const byKey: Record<string, Setting> = {};
      for (const s of res.settings) byKey[s.key] = s;
      setSettings(byKey);
      setDrafts({});
    } catch (e) {
      setMessage({ kind: 'err', text: `加载失败: ${(e as Error).message}` });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const save = async (key: string) => {
    setSaving(key);
    setMessage(null);
    try {
      const value = drafts[key] ?? '';
      await api(`/v1/admin/platform/settings/${key}`, {
        method: 'PUT',
        body: JSON.stringify({ value: value.trim() || null }),
      });
      setMessage({ kind: 'ok', text: `${key} 已保存 — 后端 cache 已刷新。` });
      await load();
    } catch (e) {
      setMessage({ kind: 'err', text: `保存失败: ${(e as Error).message}` });
    } finally {
      setSaving(null);
    }
  };

  const clearValue = async (key: string) => {
    if (!confirm(`确定要清空 ${key} 吗? (若有 env fallback, 将使用 env 值)`)) return;
    setSaving(key);
    try {
      await api(`/v1/admin/platform/settings/${key}`, {
        method: 'PUT',
        body: JSON.stringify({ value: null }),
      });
      setMessage({ kind: 'ok', text: `${key} 已清空。` });
      await load();
    } catch (e) {
      setMessage({ kind: 'err', text: `删除失败: ${(e as Error).message}` });
    } finally {
      setSaving(null);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader title="飞机机器人 设置" description="自动发送配对码所需的 bot token / username / webhook 设置。保存即生效。" />

      {message && (
        <Alert variant={message.kind === 'err' ? 'destructive' : 'default'}>
          <AlertDescription>{message.text}</AlertDescription>
        </Alert>
      )}

      <SectionCard title="Bot 凭证" description="保存到 DB — 优先于 env(.env)。清空则使用 env fallback。">
        {loading ? (
          <div className="text-sm text-muted-foreground">加载中...</div>
        ) : (
          <div className="space-y-5">
            {FIELDS.map(({ key, label, hint, placeholder }) => {
              const s = settings[key];
              const draft = drafts[key] ?? '';
              return (
                <div key={key} className="space-y-2">
                  <div className="flex items-center gap-2">
                    <label className="text-sm font-medium">{label}</label>
                    {s && (
                      <Badge variant={s.source === 'db' ? 'default' : s.source === 'env' ? 'secondary' : 'outline'}>
                        {s.source === 'db' ? 'DB 已保存' : s.source === 'env' ? '从 .env 加载' : '未设置'}
                      </Badge>
                    )}
                    {s?.hasValue && (
                      <span className="text-xs text-muted-foreground">当前值: {s.value}</span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">{hint}</p>
                  <div className="flex gap-2">
                    <Input
                      type={s?.isSecret ? 'password' : 'text'}
                      placeholder={placeholder}
                      value={draft}
                      onChange={(e) => setDrafts((d) => ({ ...d, [key]: e.target.value }))}
                      disabled={saving === key}
                    />
                    <Button onClick={() => save(key)} disabled={saving === key || draft.length === 0}>
                      {saving === key ? '保存中...' : '保存'}
                    </Button>
                    {s?.source === 'db' && (
                      <Button variant="outline" onClick={() => clearValue(key)} disabled={saving === key}>
                        清空
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </SectionCard>

      <SectionCard
        title="webhook 注册 (一次性)"
        description="保存 bot token + secret 后, 需向飞机注册 webhook URL 以接收用户 /start 消息。"
      >
        <div className="space-y-3 text-sm">
          <p>token + secret 都保存后, 在终端执行一次:</p>
          <pre className="rounded-md bg-muted p-3 text-xs overflow-x-auto">
{`curl -s "https://api.telegram.org/bot<TOKEN>/setWebhook?\\
url=https://sayknowwork.ai-ops.click/v1/auth/telegram/webhook&\\
secret_token=<SECRET>"`}
          </pre>
          <p className="text-muted-foreground">
            响应 <code>{`{"ok":true}`}</code> 即注册完成。之后用户在 bot 中 <code>/start &lt;bindCode&gt;</code> 即可自动绑定 chat_id 到 DB。
          </p>
        </div>
      </SectionCard>
    </div>
  );
}
